import { PubSubService } from '../_shared/pubSubServiceInterface';
import { ExtensionManager } from '../../extensions';
import ServicesManager from '../ServicesManager';
import ViewportGridService from '../ViewportGridService';
import { DisplaySet } from '../../types';

enum RequestType {
  /** Highest priority for loading*/
  Interaction = 'interaction',
  /** Second highest priority for loading*/
  Thumbnail = 'thumbnail',
  /** Third highest priority for loading, usually used for image loading in the background*/
  Prefetch = 'prefetch',
  /** Lower priority, often used for background computations in the worker */
  Compute = 'compute',
}

export const EVENTS = {
  SERVICE_STARTED: 'event::studyPrefetcherService:started',
  SERVICE_STOPPED: 'event::studyPrefetcherService:stopped',
  DISPLAYSET_LOAD_PROGRESS: 'event::studyPrefetcherService:displaySetLoadProgress',
  DISPLAYSET_LOAD_COMPLETE: 'event::studyPrefetcherService:displaySetLoadComplete',
};

/**
 * Order used for prefetching display set
 */
enum StudyPrefetchOrder {
  closest = 'closest',
  downward = 'downward',
  upward = 'upward',
}

/**
 * Study Prefetcher configuration
 */
type StudyPrefetcherConfig = {
  /* Enable/disable study prefetching service */
  enabled: boolean;
  /* Number of displaysets to be prefetched */
  displaySetsCount: number;
  /**
   * Max number of concurrent prefetch requests
   * High numbers may impact on the time to load a new dropped series because
   * the browser will be busy with all prefetching requests. As soon as the
   * prefetch requests get fulfilled the new ones from the new dropped series
   * are sent to the server.
   *
   * TODO: abort all prefetch requests when a new series is loaded on a viewport.
   * (need to add support for `AbortController` on Cornerstone)
   * */
  maxNumPrefetchRequests: number;
  /* Display sets prefetching order (closest, downward and upward) */
  order: StudyPrefetchOrder;
  /* Trim inactive image cache after this many study instances/frames. */
  cacheTrimInstanceThreshold: number;
  /* Number of recently opened display sets that should be protected from trimming. */
  cacheTrimRecentDisplaySetsToKeep: number;
};

type DisplaySetLoadingState = {
  displaySetInstanceUID: string;
  numInstances: number;
  pendingImageIds: Set<string>;
  loadedImageIds: Set<string>;
  failedImageIds: Set<string>;
  loadingProgress: number;
};

type ExternalDisplaySetLoadingProgress = {
  source: string;
  loadingProgress: number;
  numInstances?: number;
};

type ImageRequest = {
  displaySetInstanceUID: string;
  imageId: string;
  aborted: boolean;
  started: boolean;
};

const ACTIVE_DISPLAY_SET_PRIORITY = -10;
const BACKGROUND_PREFETCH_PRIORITY = -5;

type PubSubServiceSubscription = { unsubscribe: () => any };

interface ICache {
  isImageCached(imageId: string): boolean;
  removeImageLoadObject?: (imageId: string) => void;
}

interface IImageLoadPoolManager {
  addRequest(
    requestFn: () => Promise<any>,
    type: string,
    additionalDetails: Record<string, unknown>,
    priority?: number
  );
  clearRequestStack(type: string): void;
}

interface IImageLoader {
  loadAndCacheImage(imageId: string, options: any): Promise<any>;
}

type EventSubscription = {
  unsubscribe: () => void;
};

interface IImageLoadEventsManager {
  addEventListeners(
    onImageLoaded: (evt: any) => void,
    onImageLoadFailed: (evt: any) => void,
    onImageCacheRemoved: (evt: any) => void
  ): EventSubscription[];
}

class StudyPrefetcherService extends PubSubService {
  private _extensionManager: ExtensionManager;
  private _servicesManager: ServicesManager;
  private _subscriptions: PubSubServiceSubscription[];
  private _activeDisplaySetsInstanceUIDs: string[] = [];
  private _pendingRequests: ImageRequest[] = [];
  private _inflightRequests = new Map<string, ImageRequest>();
  private _isRunning = false;
  private _displaySetLoadingStates = new Map<string, DisplaySetLoadingState>();
  private _externalDisplaySetLoadingProgress = new Map<string, ExternalDisplaySetLoadingProgress>();
  private _imageIdsToDisplaySetsMap = new Map<string, Set<string>>();
  private _recentDisplaySetInstanceUIDs: string[] = [];
  private config: StudyPrefetcherConfig = {
    /* Enable/disable study prefetching service */
    enabled: true,
    /* Number of displaysets to be prefetched */
    displaySetsCount: 5,
    /**
     * Max number of concurrent prefetch requests
     * High numbers may impact on the time to load a new dropped series because
     * the browser will be busy with all prefetching requests. As soon as the
     * prefetch requests get fulfilled the new ones from the new dropped series
     * are sent to the server.
     *
     * TODO: abort all prefetch requests when a new series is loaded on a viewport.
     * (need to add support for `AbortController` on Cornerstone)
     * */
    maxNumPrefetchRequests: 5,
    /* Display sets prefetching order (closest, downward and upward) */
    order: StudyPrefetchOrder.closest,
    cacheTrimInstanceThreshold: 2000,
    cacheTrimRecentDisplaySetsToKeep: 3,
  };

  // Properties set by Cornerstone extension (initStudyPrefetcherService)
  public requestType: string = RequestType.Prefetch;
  public cache: ICache;
  public imageLoadPoolManager: IImageLoadPoolManager;
  public imageLoader: IImageLoader;
  public imageLoadEventsManager: IImageLoadEventsManager;

  public static REGISTRATION = {
    name: 'studyPrefetcherService',
    altName: 'StudyPrefetcherService',
    create: ({ configuration, servicesManager, extensionManager }): StudyPrefetcherService => {
      return new StudyPrefetcherService({
        servicesManager,
        extensionManager,
        configuration,
      });
    },
  };

  constructor({
    servicesManager,
    extensionManager,
    configuration,
  }: {
    servicesManager: ServicesManager;
    extensionManager: ExtensionManager;
    configuration: StudyPrefetcherConfig;
  }) {
    super(EVENTS);

    this._servicesManager = servicesManager;
    this._extensionManager = extensionManager;
    this._subscriptions = [];

    Object.assign(this.config, configuration);
  }

  public onModeEnter(): void {
    this._addEventListeners();
  }

  /**
   * The onModeExit returns the service to the initial state.
   */
  public onModeExit(): void {
    this._removeEventListeners();
    this._stopPrefetching();
    this._externalDisplaySetLoadingProgress.clear();
  }

  //添加图像加载成功和失败的事件监听器，并在图像加载完成（成功或失败）时执行相应的操作
  private _addImageLoadingEventsListeners() {
    const fnOnImageLoadCompleted = (imageId: string) => {
      // `sendNextRequests` must be called after image loaded/failed events
      // to make sure prefetch requests shall be sent as soon as the active
      // displaySets (active viewport) are loaded.
      //
      // PS: active display sets are not loaded by this service and that is why
      // the requests shall not be in the inflight queue.
      //检查当前图像 ID 是否在 _inflightRequests 映射中，如果不在，则调用 _sendNextRequests 方法，尝试发送下一批预取请求。
      if (!this._inflightRequests.get(imageId)) {
        this._sendNextRequests();
      }
    };

    //图像加载成功事件的监听器
    const fnImageLoadedEventListener = evt => {
      const { image } = evt.detail;
      const { imageId } = image;

      //调用 _moveImageIdToLoadedSet 方法，将图像 ID 从待加载集合移动到已加载集合
      this._moveImageIdToLoadedSet(imageId);
      //处理图像加载完成后的操作
      fnOnImageLoadCompleted(imageId);
    };

    const fnImageLoadFailedEventListener = evt => {
      const { imageId } = evt.detail;
      //将图像 ID 从待加载集合移动到失败集合
      this._moveImageIdToFailedSet(imageId);
      //处理图像加载完成后的操作
      fnOnImageLoadCompleted(imageId);
    };
    const fnImageCacheRemovedEventListener = evt => {
      const { imageId } = evt.detail || {};

      if (imageId) {
        this._moveImageIdBackToPendingSet(imageId);
      }
    };
    //调用 this.imageLoadEventsManager.addEventListeners 方法，将 fnImageLoadedEventListener 和 fnImageLoadFailedEventListener 作为参数传入，添加图像加载成功和失败的事件监听器，并返回事件订阅对象数组
    return this.imageLoadEventsManager.addEventListeners(
      fnImageLoadedEventListener,
      fnImageLoadFailedEventListener,
      fnImageCacheRemovedEventListener
    );
  }

  //为 displaySetService 和 viewportGridService 这两个服务的不同事件添加监听器，并返回这些事件监听器的订阅对象数组
  private _addServicesListeners() {
    const { displaySetService, viewportGridService } = this._servicesManager.services;

    // Restart the prefetcher after any change to the displaySets
    // (eg: sorting the displaySets on StudyBrowser)
    //为 displaySetService 的 DISPLAY_SETS_CHANGED 事件添加监听器
    //当 DISPLAY_SETS_CHANGED 事件触发时，会调用 this._syncWithActiveViewport 方法，并传入 { forceRestart: true } 参数，强制重启预取器。
    const displaySetsChangedSubscription = displaySetService.subscribe(
      displaySetService.EVENTS.DISPLAY_SETS_CHANGED,
      () => this._syncWithActiveViewport({ forceRestart: true })
    );

    // Loads new datasets when making a new viewport active
    //调用 viewportGridService 的 subscribe 方法，为 ACTIVE_VIEWPORT_ID_CHANGED 事件添加监听器。
    //当 ACTIVE_VIEWPORT_ID_CHANGED 事件触发时，会调用 this._syncWithActiveViewport 方法，并传入 { activeViewportId: viewportId } 参数，同步活动视口信息。
    const viewportGridActiveViewportIdSubscription = viewportGridService.subscribe(
      ViewportGridService.EVENTS.ACTIVE_VIEWPORT_ID_CHANGED,
      ({ viewportId }) => this._syncWithActiveViewport({ activeViewportId: viewportId })
    );

    // Continue loading datasets after changing the layout (eg: from 1x1 to 2x1)
    //当 LAYOUT_CHANGED 事件触发时，会调用 this._syncWithActiveViewport 方法，同步活动视口信息。
    const viewportGridLayoutChangedSubscription = viewportGridService.subscribe(
      ViewportGridService.EVENTS.LAYOUT_CHANGED,
      () => this._syncWithActiveViewport()
    );

    // Loads new datasets after loading a new display set on a viewport
    //当 GRID_STATE_CHANGED 事件触发时，会调用 this._syncWithActiveViewport 方法，同步活动视口信息。
    const viewportGridStateChangedSubscription = viewportGridService.subscribe(
      ViewportGridService.EVENTS.GRID_STATE_CHANGED,
      () => this._syncWithActiveViewport()
    );

    // Loads the first datasets right after opening the viewer
    //当 VIEWPORTS_READY 事件触发时，会先调用 this._syncWithActiveViewport 方法同步活动视口信息，然后调用 this._startPrefetching 方法开始预取显示集。
    const viewportGridViewportreadySubscription = viewportGridService.subscribe(
      ViewportGridService.EVENTS.VIEWPORTS_READY,
      ({ disableStudyPrefetch } = {}) => {
        if (disableStudyPrefetch) {
          this._stopPrefetching();
          return;
        }
        this._syncWithActiveViewport();
        this._startPrefetching();
      }
    );

    return [
      displaySetsChangedSubscription,
      viewportGridActiveViewportIdSubscription,
      viewportGridLayoutChangedSubscription,
      viewportGridStateChangedSubscription,
      viewportGridViewportreadySubscription,
    ];
  }

  //将图像加载事件和服务相关事件的监听器添加到系统中，并将这些监听器的订阅对象收集到 this._subscriptions 数组中，以便后续统一管理和清理。
  private _addEventListeners() {
    //调用 _addImageLoadingEventsListeners 方法，该方法会添加图像加载成功和失败的事件监听器，并返回这些事件监听器的订阅对象数组，存储在 imageLoadingEventsSubscriptions 变量中。
    const imageLoadingEventsSubscriptions = this._addImageLoadingEventsListeners();
    //该方法会添加与服务相关的事件监听器，如显示集变化、视口网格状态变化等事件的监听器，并返回这些事件监听器的订阅对象数组，存储在 servicesSubscriptions 变量中。
    const servicesSubscriptions = this._addServicesListeners();

    this._subscriptions.push(...imageLoadingEventsSubscriptions);
    this._subscriptions.push(...servicesSubscriptions);
  }

  private _removeEventListeners() {
    this._subscriptions.forEach(subscription => subscription.unsubscribe());
    this._subscriptions = [];
  }

  private _syncWithActiveViewport({
    activeViewportId,
    forceRestart,
  }: {
    activeViewportId?: string;
    forceRestart?: boolean;
  } = {}) {
    const { viewportGridService } = this._servicesManager.services;
    const viewportGridServiceState = viewportGridService.getState();
    const { viewports } = viewportGridServiceState;

    activeViewportId = activeViewportId ?? viewportGridServiceState.activeViewportId;

    // If may be null when the viewer is loaded
    if (!activeViewportId) {
      return;
    }

    const activeViewport = viewports.get(activeViewportId);

    if (!activeViewport) {
      return;
    }

    const displaySetUpdated = this._setActiveDisplaySetsUIDs(activeViewport.displaySetInstanceUIDs);

    if (forceRestart) {
      this._restartPrefetching();
    } else if (displaySetUpdated) {
      this._reprioritizePrefetching();
    }
  }

  private _setActiveDisplaySetsUIDs(newActiveDisplaySetInstanceUIDs: string[]): boolean {
    //检查新旧显示集是否相同
    //首先比较新旧数组的长度是否相等。
    //然后使用 every 方法遍历新数组中的每个 UID，检查它是否存在于旧数组中。如果所有 UID 都存在，则 sameDisplaySets 为 true，否则为 false。
    const sameDisplaySets =
      newActiveDisplaySetInstanceUIDs.length === this._activeDisplaySetsInstanceUIDs.length &&
      newActiveDisplaySetInstanceUIDs.every(uid =>
        this._activeDisplaySetsInstanceUIDs.includes(uid)
      );

    //如果显示集未变化，返回 false
    if (sameDisplaySets) {
      return false;
    }

    //更新活动显示集的实例 UID 数组并重启预取操作
    this._activeDisplaySetsInstanceUIDs = [...newActiveDisplaySetInstanceUIDs];
    newActiveDisplaySetInstanceUIDs.forEach(displaySetInstanceUID =>
      this._rememberDisplaySetInstanceUID(displaySetInstanceUID)
    );
    return true;
  }

  private _rememberDisplaySetInstanceUID(displaySetInstanceUID?: string): void {
    if (!displaySetInstanceUID) {
      return;
    }

    this._recentDisplaySetInstanceUIDs = [
      displaySetInstanceUID,
      ...this._recentDisplaySetInstanceUIDs.filter(uid => uid !== displaySetInstanceUID),
    ].slice(0, Math.max(1, this.config.cacheTrimRecentDisplaySetsToKeep));
  }

  private _isActiveDisplaySet(displaySetInstanceUID: string): boolean {
    return this._activeDisplaySetsInstanceUIDs.includes(displaySetInstanceUID);
  }

  private _getClosestDisplaySets(displaySets: DisplaySet[], activeDisplaySetIndex: number) {
    const sortedDisplaySets = [];
    let previousIndex = activeDisplaySetIndex - 1;
    let nextIndex = activeDisplaySetIndex + 1;

    while (previousIndex >= 0 || nextIndex < displaySets.length) {
      if (previousIndex >= 0) {
        sortedDisplaySets.push(displaySets[previousIndex]);
        previousIndex--;
      }

      if (nextIndex < displaySets.length) {
        sortedDisplaySets.push(displaySets[nextIndex]);
        nextIndex++;
      }
    }

    return sortedDisplaySets;
  }

  private _getDownwardDisplaySets(displaySets: DisplaySet[], activeDisplaySetIndex: number) {
    const sortedDisplaySets = [];

    for (let i = activeDisplaySetIndex + 1; i < displaySets.length; i++) {
      sortedDisplaySets.push(displaySets[i]);
    }

    return sortedDisplaySets;
  }

  private _getUpwardDisplaySets(displaySets: DisplaySet[], activeDisplaySetIndex: number) {
    const sortedDisplaySets = [];

    for (let i = activeDisplaySetIndex - 1; i >= 0 && i !== activeDisplaySetIndex; i--) {
      sortedDisplaySets.push(displaySets[i]);
    }

    return sortedDisplaySets;
  }

  private _getSortedDisplaySetsToPrefetch(displaySets: DisplaySet[]): DisplaySet[] {
    if (!this._activeDisplaySetsInstanceUIDs?.length) {
      return [];
    }

    const { displaySetsCount } = this.config;
    const activeDisplaySetsInstanceUIDs = this._activeDisplaySetsInstanceUIDs;
    const [activeDisplaySetUID] = activeDisplaySetsInstanceUIDs;
    const activeDisplaySetIndex = displaySets.findIndex(
      ds => ds.displaySetInstanceUID === activeDisplaySetUID
    );
    const getDisplaySetsFunctionsMap = {
      [StudyPrefetchOrder.closest]: this._getClosestDisplaySets,
      [StudyPrefetchOrder.downward]: this._getDownwardDisplaySets,
      [StudyPrefetchOrder.upward]: this._getUpwardDisplaySets,
    };
    const { order } = this.config;
    const fnGetDisplaySets = getDisplaySetsFunctionsMap[order];

    if (!fnGetDisplaySets) {
      throw new Error(`Invalid order (${order})`);
    }

    // Creates a `Set` to look for UIDs in O(1) instead of O(n)
    const uidsSet = new Set(activeDisplaySetsInstanceUIDs);

    // Remove any active displaySet that may still be in the activeDisplaySetsInstanceUIDs.
    // That may happen when activeDisplaySetsInstanceUIDs has more than one element.
    return fnGetDisplaySets
      .call(this, displaySets, activeDisplaySetIndex)
      .filter(ds => !uidsSet.has(ds.displaySetInstanceUID))
      .slice(0, displaySetsCount);
  }

  private _getDisplaySets() {
    const { displaySetService } = this._servicesManager.services;
    const displaySets = [...displaySetService.getActiveDisplaySets()];
    const displaySetsToPrefetch = this._getSortedDisplaySetsToPrefetch(displaySets);

    return { displaySets, displaySetsToPrefetch };
  }

  //该方法的主要功能是更新 _imageIdsToDisplaySetsMap 映射，确保每个图像 ID 都有一个对应的显示集实例 UID 集合，并且将传入的显示集实例 UID 添加到相应的集合中。这样可以方便后续根据图像 ID 查找其所属的显示集实例 UID。
  private _updateImageIdsDisplaySetMap(displaySetInstanceUID: string, imageIds: string[]): void {
    for (const imageId of imageIds) {
      let displaySetsInstanceUIDsMap = this._imageIdsToDisplaySetsMap.get(imageId);

      if (!displaySetsInstanceUIDsMap) {
        displaySetsInstanceUIDsMap = new Set();
        this._imageIdsToDisplaySetsMap.set(imageId, displaySetsInstanceUIDsMap);
      }

      displaySetsInstanceUIDsMap.add(displaySetInstanceUID);
    }
  }

  private _getImageIdsForDisplaySet(displaySet: DisplaySet): string[] {
    const dataSource = this._extensionManager.getActiveDataSource()[0];

    return dataSource.getImageIdsForDisplaySet(displaySet); //获取图像id
  }

  private _updateDisplaySetLoadingProgress(displaySetLoadingState: DisplaySetLoadingState) {
    const { numInstances, loadedImageIds } = displaySetLoadingState;
    const loadingProgress = numInstances > 0 ? loadedImageIds.size / numInstances : 0;

    displaySetLoadingState.loadingProgress = loadingProgress;
  }

  //根据准备加载的图片和已经加载的图片，计算加载进度
  private _addDisplaySetLoadingState(displaySet: DisplaySet, publishInitialProgress = true): void {
    const { displaySetInstanceUID } = displaySet;
    const imageIds = this._getImageIdsForDisplaySet(displaySet); //获取图片id
    let displaySetLoadingState = this._displaySetLoadingStates.get(displaySetInstanceUID);

    if (displaySetLoadingState) {
      return;
    }

    const pendingImageIds = new Set<string>(); //准备加载的图片
    const loadedImageIds = new Set<string>(); //加载成功的图片

    // Needs to check which image is already loaded to update the progress properly
    // because some images may already be loaded (thumbnails and viewports).
    for (const imageId of imageIds) {
      if (this.cache.isImageCached(imageId)) {
        loadedImageIds.add(imageId);
      } else {
        pendingImageIds.add(imageId);
      }
    }

    displaySetLoadingState = {
      displaySetInstanceUID,
      numInstances: imageIds.length,
      pendingImageIds,
      loadedImageIds,
      failedImageIds: new Set(),
      loadingProgress: 0,
    };

    this._updateDisplaySetLoadingProgress(displaySetLoadingState);
    this._displaySetLoadingStates.set(displaySetInstanceUID, displaySetLoadingState);
    this._updateImageIdsDisplaySetMap(displaySetInstanceUID, imageIds);

    if (publishInitialProgress) {
      // Publish the initial progress so newly mounted series lists can render 0% immediately.
      this._triggerDisplaySetEvents(displaySetInstanceUID);
    }
  }

  public getDisplaySetLoadingProgress(displaySetInstanceUID: string): number | undefined {
    const externalProgress = this._externalDisplaySetLoadingProgress.get(displaySetInstanceUID);

    if (externalProgress) {
      return externalProgress.loadingProgress;
    }

    if (!this._displaySetLoadingStates.has(displaySetInstanceUID)) {
      const { displaySetService } = this._servicesManager.services;
      const displaySet = displaySetService
        .getActiveDisplaySets()
        .find(displaySet => displaySet.displaySetInstanceUID === displaySetInstanceUID);

      if (displaySet) {
        this._addDisplaySetLoadingState(displaySet, false);
      }
    }

    return this._displaySetLoadingStates.get(displaySetInstanceUID)?.loadingProgress;
  }

  /**
   * Reconcile the stored progress with the images that are reusable in the Cornerstone cache.
   * Loading callbacks can arrive before a later cache eviction (or an eviction event can be
   * missed), so a stored 100% is not sufficient evidence that opening the display set is offline.
   */
  public reconcileDisplaySetLoadingProgress(displaySetInstanceUID: string): number | undefined {
    const externalProgress = this._externalDisplaySetLoadingProgress.get(displaySetInstanceUID);

    // External viewers own both their cache and their progress lifecycle.
    if (externalProgress) {
      return externalProgress.loadingProgress;
    }

    if (!this.cache?.isImageCached) {
      return this.getDisplaySetLoadingProgress(displaySetInstanceUID);
    }

    const { displaySetService } = this._servicesManager.services;
    const displaySet = displaySetService
      .getActiveDisplaySets()
      .find(displaySet => displaySet.displaySetInstanceUID === displaySetInstanceUID);

    if (!displaySet) {
      return undefined;
    }

    this._addDisplaySetLoadingState(displaySet, false);

    const displaySetLoadingState = this._displaySetLoadingStates.get(displaySetInstanceUID);
    const imageIds = this._getImageIdsForDisplaySet(displaySet);
    const previousNumInstances = displaySetLoadingState.numInstances;
    const previousProgress = displaySetLoadingState.loadingProgress;
    const previousLoadedImageIds = displaySetLoadingState.loadedImageIds;
    const previousPendingImageIds = displaySetLoadingState.pendingImageIds;
    const previousFailedImageIds = displaySetLoadingState.failedImageIds;
    const loadedImageIds = new Set<string>();
    const pendingImageIds = new Set<string>();
    const failedImageIds = new Set<string>();

    for (const imageId of imageIds) {
      if (this.cache.isImageCached(imageId)) {
        loadedImageIds.add(imageId);
      } else if (previousFailedImageIds.has(imageId)) {
        failedImageIds.add(imageId);
      } else {
        pendingImageIds.add(imageId);
      }
    }

    displaySetLoadingState.numInstances = imageIds.length;
    displaySetLoadingState.loadedImageIds = loadedImageIds;
    displaySetLoadingState.pendingImageIds = pendingImageIds;
    displaySetLoadingState.failedImageIds = failedImageIds;
    this._updateDisplaySetLoadingProgress(displaySetLoadingState);
    this._updateImageIdsDisplaySetMap(displaySetInstanceUID, imageIds);

    const setChanged = (previous: Set<string>, current: Set<string>) =>
      previous.size !== current.size || Array.from(previous).some(imageId => !current.has(imageId));
    const stateChanged =
      previousProgress !== displaySetLoadingState.loadingProgress ||
      setChanged(previousLoadedImageIds, loadedImageIds) ||
      setChanged(previousPendingImageIds, pendingImageIds) ||
      setChanged(previousFailedImageIds, failedImageIds) ||
      previousNumInstances !== displaySetLoadingState.numInstances;

    if (stateChanged) {
      this._triggerDisplaySetEvents(displaySetInstanceUID);
    }

    return displaySetLoadingState.loadingProgress;
  }

  public setExternalDisplaySetLoadingProgress(
    displaySetInstanceUID: string,
    loadingProgress: number,
    { source, numInstances }: { source: string; numInstances?: number }
  ): void {
    const normalizedProgress = Math.max(0, Math.min(1, loadingProgress));

    this._externalDisplaySetLoadingProgress.set(displaySetInstanceUID, {
      source,
      loadingProgress: normalizedProgress,
      numInstances,
    });
    this._broadcastEvent(this.EVENTS.DISPLAYSET_LOAD_PROGRESS, {
      displaySetInstanceUID,
      numInstances,
      loadingProgress: normalizedProgress,
      source,
      status: normalizedProgress >= 1 ? 'complete' : 'loading',
      failedCount: 0,
    });

    if (normalizedProgress >= 1) {
      this._broadcastEvent(this.EVENTS.DISPLAYSET_LOAD_COMPLETE, {
        displaySetInstanceUID,
        source,
      });
    }
  }

  public clearExternalDisplaySetLoadingProgress(
    source: string,
    displaySetInstanceUID?: string
  ): void {
    const displaySetInstanceUIDs = displaySetInstanceUID
      ? [displaySetInstanceUID]
      : Array.from(this._externalDisplaySetLoadingProgress.keys());

    displaySetInstanceUIDs.forEach(currentDisplaySetInstanceUID => {
      const externalProgress = this._externalDisplaySetLoadingProgress.get(
        currentDisplaySetInstanceUID
      );

      if (!externalProgress || externalProgress.source !== source) {
        return;
      }

      this._externalDisplaySetLoadingProgress.delete(currentDisplaySetInstanceUID);

      if (this._displaySetLoadingStates.has(currentDisplaySetInstanceUID)) {
        this._triggerDisplaySetEvents(currentDisplaySetInstanceUID);
      }
    });
  }

  public prepareForDisplaySetChange(displaySetInstanceUID?: string): void {
    this._rememberDisplaySetInstanceUID(displaySetInstanceUID);

    const { displaySets } = this._getDisplaySets();
    const totalInstances = displaySets.reduce(
      (total, displaySet) => total + this._getDisplaySetImageCount(displaySet),
      0
    );

    if (totalInstances > this.config.cacheTrimInstanceThreshold) {
      this._trimInactiveDisplaySetCache(displaySets, displaySetInstanceUID);
    }

    if (displaySetInstanceUID) {
      this.reconcileDisplaySetLoadingProgress(displaySetInstanceUID);
    }
  }

  private _getDisplaySetImageCount(displaySet: DisplaySet): number {
    if (typeof displaySet.numImageFrames === 'number') {
      return displaySet.numImageFrames;
    }

    if (Array.isArray(displaySet.instances)) {
      return displaySet.instances.length;
    }

    return this._getImageIdsForDisplaySet(displaySet).length;
  }

  private _trimInactiveDisplaySetCache(
    displaySets: DisplaySet[],
    targetDisplaySetInstanceUID?: string
  ): void {
    if (!this.cache.removeImageLoadObject) {
      return;
    }

    const protectedDisplaySetInstanceUIDs = new Set<string>(
      [
        targetDisplaySetInstanceUID,
        ...this._activeDisplaySetsInstanceUIDs,
        ...this._recentDisplaySetInstanceUIDs,
      ].filter((displaySetInstanceUID): displaySetInstanceUID is string =>
        Boolean(displaySetInstanceUID)
      )
    );
    const evictableDisplaySetInstanceUIDs = new Set<string>();

    for (const displaySet of displaySets) {
      const { displaySetInstanceUID } = displaySet;

      if (!displaySetInstanceUID || protectedDisplaySetInstanceUIDs.has(displaySetInstanceUID)) {
        continue;
      }

      evictableDisplaySetInstanceUIDs.add(displaySetInstanceUID);
      this._removePendingRequestsForDisplaySet(displaySetInstanceUID);
      this._trimDisplaySetImageCache(displaySet, protectedDisplaySetInstanceUIDs);
    }

    this._markInflightRequestsAborted(evictableDisplaySetInstanceUIDs);
  }

  private _removePendingRequestsForDisplaySet(displaySetInstanceUID: string): void {
    this._pendingRequests = this._pendingRequests.filter(
      request => request.displaySetInstanceUID !== displaySetInstanceUID
    );
  }

  private _markInflightRequestsAborted(displaySetInstanceUIDs: Set<string>): void {
    this._inflightRequests.forEach(request => {
      if (!displaySetInstanceUIDs.has(request.displaySetInstanceUID)) {
        return;
      }

      request.aborted = true;
    });
  }

  private _trimDisplaySetImageCache(
    displaySet: DisplaySet,
    protectedDisplaySetInstanceUIDs: Set<string>
  ): void {
    const { displaySetInstanceUID } = displaySet;
    const imageIds = this._getImageIdsForDisplaySet(displaySet);
    const removedImageIds: string[] = [];

    for (let index = 1; index < imageIds.length; index++) {
      const imageId = imageIds[index];

      if (
        !this.cache.isImageCached(imageId) ||
        this._isImageIdProtected(imageId, protectedDisplaySetInstanceUIDs)
      ) {
        continue;
      }

      this.cache.removeImageLoadObject?.(imageId);
      removedImageIds.push(imageId);
    }

    if (removedImageIds.length) {
      this._moveImageIdsBackToPendingSet(displaySetInstanceUID, removedImageIds);
    }
  }

  private _isImageIdProtected(
    imageId: string,
    protectedDisplaySetInstanceUIDs: Set<string>
  ): boolean {
    const ownerDisplaySetInstanceUIDs = this._imageIdsToDisplaySetsMap.get(imageId);

    if (!ownerDisplaySetInstanceUIDs) {
      return false;
    }

    return Array.from(ownerDisplaySetInstanceUIDs).some(displaySetInstanceUID =>
      protectedDisplaySetInstanceUIDs.has(displaySetInstanceUID)
    );
  }

  private _moveImageIdsBackToPendingSet(displaySetInstanceUID: string, imageIds: string[]): void {
    const displaySetLoadingState = this._displaySetLoadingStates.get(displaySetInstanceUID);

    if (!displaySetLoadingState) {
      return;
    }

    let changed = false;
    for (const imageId of imageIds) {
      if (displaySetLoadingState.loadedImageIds.delete(imageId)) {
        displaySetLoadingState.pendingImageIds.add(imageId);
        changed = true;
      }
    }

    if (!changed) {
      return;
    }

    this._updateDisplaySetLoadingProgress(displaySetLoadingState);
    this._triggerDisplaySetEvents(displaySetInstanceUID);
  }

  private _moveImageIdBackToPendingSet(imageId: string): boolean {
    const displaySetInstanceUIDs = this._imageIdsToDisplaySetsMap.get(imageId);

    if (!displaySetInstanceUIDs) {
      return false;
    }

    let changed = false;

    for (const displaySetInstanceUID of displaySetInstanceUIDs) {
      const displaySetLoadingState = this._displaySetLoadingStates.get(displaySetInstanceUID);

      if (!displaySetLoadingState || !displaySetLoadingState.loadedImageIds.delete(imageId)) {
        continue;
      }

      displaySetLoadingState.pendingImageIds.add(imageId);
      this._updateDisplaySetLoadingProgress(displaySetLoadingState);
      this._triggerDisplaySetEvents(displaySetInstanceUID);
      changed = true;
    }

    return changed;
  }

  public deleteCache() {
    this.prepareForDisplaySetChange(this._activeDisplaySetsInstanceUIDs[0]);
  }

  private _loadDisplaySets() {
    //displaySets是所有的影像序列的集合
    //displaySetsToPrefetch 包含需要预取的显示集。
    const { displaySets, displaySetsToPrefetch } = this._getDisplaySets();
    const displaySetsByUID = new Map(
      displaySets.map(displaySet => [displaySet.displaySetInstanceUID, displaySet])
    );
    const activeDisplaySets = this._activeDisplaySetsInstanceUIDs
      .map(displaySetInstanceUID => displaySetsByUID.get(displaySetInstanceUID))
      .filter((displaySet): displaySet is DisplaySet => Boolean(displaySet));

    //遍历影像序列集合分别去设置每个序列的进度条
    displaySets.forEach(displaySet => this._addDisplaySetLoadingState(displaySet));

    if (this.config.displaySetsCount <= 0) {
      return;
    }

    [...activeDisplaySets]
      .reverse()
      .forEach(displaySet => this._enqueueDisplaySetImagesRequests(displaySet, { toFront: true }));
    displaySetsToPrefetch.forEach(displaySet => this._enqueueDisplaySetImagesRequests(displaySet));

    // Initial state is created before requests are queued. Publish once more so consumers can
    // distinguish actively loading display sets from uncached display sets that are not scheduled.
    displaySets.forEach(displaySet =>
      this._triggerDisplaySetEvents(displaySet.displaySetInstanceUID)
    );
  }

  //将指定的图像 ID 从待加载集合移动到已加载集合，并更新相应显示集的加载进度和触发相关事件。
  private _moveImageIdToLoadedSet(imageId: string): boolean {
    const displaySetsInstanceUIDs = this._imageIdsToDisplaySetsMap.get(imageId);

    if (!displaySetsInstanceUIDs) {
      return;
    }

    for (const displaySetInstanceUID of Array.from(displaySetsInstanceUIDs.values())) {
      const displaySetLoadingState = this._displaySetLoadingStates.get(displaySetInstanceUID);
      const { pendingImageIds, loadedImageIds, failedImageIds } = displaySetLoadingState;

      pendingImageIds.delete(imageId);
      failedImageIds.delete(imageId);
      loadedImageIds.add(imageId);

      this._updateDisplaySetLoadingProgress(displaySetLoadingState);
      this._triggerDisplaySetEvents(displaySetInstanceUID);
    }

    return true;
  }

  private _moveImageIdToFailedSet(imageId: string): boolean {
    const displaySetsInstanceUIDs = this._imageIdsToDisplaySetsMap.get(imageId);

    if (!displaySetsInstanceUIDs) {
      return;
    }

    for (const displaySetInstanceUID of Array.from(displaySetsInstanceUIDs.values())) {
      const displaySetLoadingState = this._displaySetLoadingStates.get(displaySetInstanceUID);
      const { pendingImageIds, loadedImageIds, failedImageIds } = displaySetLoadingState;

      pendingImageIds.delete(imageId);
      loadedImageIds.delete(imageId);
      failedImageIds.add(imageId);

      this._updateDisplaySetLoadingProgress(displaySetLoadingState);
      this._triggerDisplaySetEvents(displaySetInstanceUID);
    }

    return true;
  }

  private _getDisplaySetLoadingStatus(
    displaySetLoadingState: DisplaySetLoadingState
  ): 'complete' | 'loading' | 'failed' | 'not-cached' {
    if (displaySetLoadingState.loadingProgress >= 1) {
      return 'complete';
    }

    const { displaySetInstanceUID } = displaySetLoadingState;
    const hasScheduledRequests =
      this._pendingRequests.some(
        request => request.displaySetInstanceUID === displaySetInstanceUID
      ) ||
      Array.from(this._inflightRequests.values()).some(
        request => request.displaySetInstanceUID === displaySetInstanceUID && !request.aborted
      );

    if (hasScheduledRequests) {
      return 'loading';
    }

    if (displaySetLoadingState.failedImageIds.size > 0) {
      return 'failed';
    }

    return 'not-cached';
  }

  private _triggerDisplaySetEvents(displaySetInstanceUID: string) {
    const displaySetLoadingState = this._displaySetLoadingStates.get(displaySetInstanceUID);
    const externalProgress = this._externalDisplaySetLoadingProgress.get(displaySetInstanceUID);
    const loadingProgress =
      externalProgress?.loadingProgress ?? displaySetLoadingState.loadingProgress;
    const numInstances = externalProgress?.numInstances ?? displaySetLoadingState.numInstances;

    this._broadcastEvent(this.EVENTS.DISPLAYSET_LOAD_PROGRESS, {
      displaySetInstanceUID,
      numInstances,
      loadingProgress,
      source: externalProgress?.source,
      status: externalProgress
        ? externalProgress.loadingProgress >= 1
          ? 'complete'
          : 'loading'
        : this._getDisplaySetLoadingStatus(displaySetLoadingState),
      failedCount: externalProgress ? 0 : displaySetLoadingState.failedImageIds.size,
    });

    if (loadingProgress >= 1) {
      this._broadcastEvent(this.EVENTS.DISPLAYSET_LOAD_COMPLETE, {
        displaySetInstanceUID,
      });
    }
  }

  private _releaseInflightRequest(imageRequest: ImageRequest): boolean {
    if (this._inflightRequests.get(imageRequest.imageId) !== imageRequest) {
      return false;
    }

    this._inflightRequests.delete(imageRequest.imageId);
    return true;
  }

  //用于处理图像预取成功的情况
  private _onImagePrefetchSuccess(imageRequest: ImageRequest) {
    //检查请求是否已中止
    //如果请求已中止，则返回，不执行后续操作。
    if (imageRequest.aborted) {
      this._releaseInflightRequest(imageRequest);
      if (this._displaySetLoadingStates.has(imageRequest.displaySetInstanceUID)) {
        this._triggerDisplaySetEvents(imageRequest.displaySetInstanceUID);
      }
      this._sendNextRequests();
      return;
    }

    const { imageId } = imageRequest;

    //从正在进行的请求映射中删除该图像 ID 对应的请求。这表示该请求已经完成，不再处于进行中状态。
    this._releaseInflightRequest(imageRequest);
    //将该图像 ID 从待加载集合移动到已加载集合，并更新相应显示集的加载进度和触发相关事件。
    this._moveImageIdToLoadedSet(imageId);

    // `sendNextRequests` must be called after removing the request from the inflight
    // queue otherwise it shall not be able to send the request (maxNumPrefetchRequests)
    this._sendNextRequests();
  }

  private _onImagePrefetchFailed(imageRequest, error) {
    if (imageRequest.aborted) {
      this._releaseInflightRequest(imageRequest);
      if (this._displaySetLoadingStates.has(imageRequest.displaySetInstanceUID)) {
        this._triggerDisplaySetEvents(imageRequest.displaySetInstanceUID);
      }
      this._sendNextRequests();
      return;
    }

    console.warn(`An error ocurred when trying to load "${imageRequest.imageId}"`, error);

    const { imageId } = imageRequest;

    this._releaseInflightRequest(imageRequest);
    this._moveImageIdToFailedSet(imageId);

    // `sendNextRequests` must be called after removing the request from the inflight
    // queue otherwise it shall not be able to send the request (maxNumPrefetchRequests)
    this._sendNextRequests();
  }

  //在满足一定条件时，从待处理请求队列中取出图像请求并发送这些请求以进行预取操作
  private async _sendNextRequests() {
    // If the service has stopped with async requests in progress this method may
    // get called again when each of those requests are fulfilled.
    //如果服务没有在运行状态（this._isRunning 为 false），则直接返回，不执行后续操作。
    //这是为了避免在服务停止时，仍有异步请求完成后调用该方法导致不必要的操作。
    if (!this._isRunning) {
      return;
    }

    // Active display sets are queued first, so visible images keep priority without
    // waiting for the user to scroll through the whole stack.
    //从 this 对象中解构出待处理请求数组 pendingRequests 和正在进行中的请求映射 inflightRequests。
    const { _pendingRequests: pendingRequests, _inflightRequests: inflightRequests } = this;
    //从配置中获取最大预取请求数 maxNumPrefetchRequests。
    const { maxNumPrefetchRequests } = this.config;
    //如果待处理请求数组为空，或者正在进行中的请求数量已经达到或超过最大预取请求数，则直接返回，不发送新的请求。
    if (!pendingRequests.length || inflightRequests.size >= maxNumPrefetchRequests) {
      return;
    }
    //计算要发送的请求数量 numImageRequests，取待处理请求数量和剩余可发送请求数量（最大预取请求数减去正在进行中的请求数量）的较小值。
    const numImageRequests = Math.min(
      pendingRequests.length,
      maxNumPrefetchRequests - inflightRequests.size
    );
    const imageRequests = this._pendingRequests.splice(0, numImageRequests);

    //遍历 imageRequests 数组中的每个请求
    imageRequests.forEach(imageRequest => {
      const { imageId } = imageRequest;
      const priority = this._isActiveDisplaySet(imageRequest.displaySetInstanceUID)
        ? ACTIVE_DISPLAY_SET_PRIORITY
        : BACKGROUND_PREFETCH_PRIORITY;
      const options = {
        priority,
        requestType: this.requestType,
        additionalDetails: { imageId },
        preScale: {
          enabled: true,
        },
      };

      this.imageLoadPoolManager.addRequest(
        //请求数据
        async () => {
          imageRequest.started = true;

          if (imageRequest.aborted) {
            this._releaseInflightRequest(imageRequest);
            this._sendNextRequests();
            return;
          }

          if (this.cache.isImageCached(imageId)) {
            this._onImagePrefetchSuccess(imageRequest);
            return;
          }

          return this.imageLoader.loadAndCacheImage(imageId, options).then(
            _image => this._onImagePrefetchSuccess(imageRequest),
            error => this._onImagePrefetchFailed(imageRequest, error)
          );
        },
        this.requestType,
        { imageId },
        priority
      );

      inflightRequests.set(imageId, imageRequest);
    });
  }

  //将指定显示集（DisplaySet）中的图像请求添加到待处理请求队列中
  private _enqueueDisplaySetImagesRequests(
    displaySet: DisplaySet,
    options: { toFront?: boolean } = {}
  ) {
    const { displaySetInstanceUID } = displaySet;
    //调用 this._getImageIdsForDisplaySet 方法，传入 displaySet 作为参数，获取该显示集中的所有图像 ID。这些图像 ID 通常是请求图像数据的地址。
    const imageIds = this._getImageIdsForDisplaySet(displaySet); //imageIds,存放的是图片的id也是请求地址

    const imageIdsSet = new Set(imageIds);
    const imageRequests: ImageRequest[] = [];

    imageIds.forEach(imageId => {
      //检查当前图像是否已经缓存在本地。如果已经缓存，则调用 this._moveImageIdToLoadedSet(imageId) 方法将该图像 ID 标记为已加载，并使用 return 语句跳过后续操作。
      if (this.cache.isImageCached(imageId)) {
        this._moveImageIdToLoadedSet(imageId);
        return;
      }
      if (this._inflightRequests.has(imageId)) {
        return;
      }
      //如果图像未缓存，则创建一个包含 displaySetInstanceUID、imageId 和 aborted（初始值为 false）的对象，并将其添加到 this._pendingRequests 数组中，表示该图像请求待处理。
      imageRequests.push({
        displaySetInstanceUID,
        imageId,
        aborted: false,
        started: false,
      });
    });

    this._pendingRequests = this._pendingRequests.filter(
      request => !imageIdsSet.has(request.imageId)
    );

    if (options.toFront) {
      this._pendingRequests.unshift(...imageRequests);
      return;
    }

    this._pendingRequests.push(...imageRequests);
  }

  /**
   * Start prefetching the display sets based on the active viewport and app configuration.
   */
  private _startPrefetching(): void {
    if (this._isRunning) {
      return;
    }

    if (!this.config.enabled) {
      console.log('StudyPrefetcher is not enabled');
      return;
    }

    this._isRunning = true;

    this._loadDisplaySets();
    this._sendNextRequests();
    this._broadcastEvent(this.EVENTS.SERVICE_STARTED, {});
  }

  /**
   * Stop prefetching the display sets.
   * All internal variables are cleared but activeDisplaySetsInstanceUIDs otherwise restart would not work.
   */
  private _stopPrefetching(): void {
    if (!this._isRunning) {
      return;
    }
    this._isRunning = false;

    // Running requests cannot be cancelled by clearing the load-pool stack. Keep them
    // registered until their callbacks settle so a restart cannot enqueue the same imageId.
    // Requests that have not started are removed because clearRequestStack drops their callbacks.
    this._inflightRequests.forEach((inflightRequest, imageId) => {
      inflightRequest.aborted = true;

      if (!inflightRequest.started) {
        this._inflightRequests.delete(imageId);
      }
    });

    this._pendingRequests = [];
    this._displaySetLoadingStates.clear();
    this._imageIdsToDisplaySetsMap.clear();
    this.imageLoadPoolManager.clearRequestStack(this.requestType);

    this._broadcastEvent(this.EVENTS.SERVICE_STOPPED, {});
  }

  /**
   * Restart prefetching in case it is already running.
   */
  private _restartPrefetching(): void {
    if (this._isRunning) {
      this._stopPrefetching();
      this._startPrefetching();
    }
  }

  /**
   * Rebuild the prefetch queue after the active viewport changes without
   * discarding display set progress. This keeps already loaded series progress
   * stable while still moving the newly selected display set to the front.
   */
  private _reprioritizePrefetching(): void {
    if (!this._isRunning) {
      return;
    }

    this._pendingRequests = [];
    this.imageLoadPoolManager.clearRequestStack(this.requestType);
    this._loadDisplaySets();
    this._sendNextRequests();
  }
}

export { StudyPrefetcherService as default, StudyPrefetcherService };
